// ============================================================================
// Facebook Page — comments / moderation capability methods.
//
// Endpoints used:
//   - GET    /{post-id}/comments      — list top-level comments
//   - POST   /{post-id}/comments      — create a comment
//   - POST   /{comment-id}/comments   — create a reply
//   - POST   /{comment-id}            — update our/Page comment
//   - DELETE /{comment-id}            — delete a comment
// ============================================================================

import type {
  SocialComment,
  SocialCreateCommentArgs,
  SocialCreateCommentResult,
  SocialDeleteCommentArgs,
  SocialDeleteCommentResult,
  SocialListCommentsArgs,
  SocialListCommentsResult,
  SocialPostingCallCtx,
  SocialResult,
  SocialUpdateCommentArgs,
  SocialUpdateCommentResult,
} from '../../capabilities/social-posting'
import type { RateLimitCheck } from './rate-limit-check'
import { errorFromResponse, sanitiseMetaErrorString } from './errors'
import { FB_PAGE_GRAPH_API_BASE } from './oauth'
import type {
  FacebookPageCommentMutationResponse,
  FacebookPageCommentRow,
  FacebookPageCommentsResponse,
  FacebookPageDeleteResponse,
} from './types'

const ENDPOINT_LABEL = 'facebook:page_comments'
const DEFAULT_LIMIT = 50
const MAX_LIMIT = 100

export interface FacebookPageCommentsDeps {
  fetchImpl?: typeof fetch
  graphApiBase?: string
  rateLimitCheck?: RateLimitCheck
  now?: () => Date
}

export async function listComments(
  args: SocialListCommentsArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPageCommentsDeps = {},
): Promise<SocialResult<SocialListCommentsResult>> {
  const validation = validateTokenAndPost(args.providerPostId, args.targetAccountId, pageAccessToken)
  if (!validation.ok) return validation

  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialListCommentsResult>

  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  const params = new URLSearchParams({
    fields: [
      'id',
      'message',
      'created_time',
      'from{id,name,picture}',
      'like_count',
      'comment_count',
      'parent',
      'permalink_url',
      'can_remove',
      'can_comment',
    ].join(','),
    filter: 'stream',
    limit: String(clampLimit(args.limit)),
  })
  if (typeof args.pageToken === 'string' && args.pageToken.length > 0) {
    params.set('after', args.pageToken)
  }

  const response = await graphFetch(
    `${base}/${encodeURIComponent(args.providerPostId)}/comments?${params.toString()}`,
    pageAccessToken,
    deps.fetchImpl,
  )
  if (!response.ok) {
    return { ok: false, error: await errorFromResponse(response, 'Facebook Graph comments failed') }
  }

  const body = (await response.json().catch(() => null)) as FacebookPageCommentsResponse | null
  if (!body || !Array.isArray(body.data)) {
    return unknownError('Facebook comments returned no data array')
  }

  return {
    ok: true,
    data: {
      comments: body.data.map((row) => toSocialComment(row, args.providerPostId)),
      nextPageToken: body.paging?.cursors?.after,
    },
  }
}

export async function createComment(
  args: SocialCreateCommentArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPageCommentsDeps = {},
): Promise<SocialResult<SocialCreateCommentResult>> {
  const validation = validateTokenAndPost(args.providerPostId, args.targetAccountId, pageAccessToken)
  if (!validation.ok) return validation
  if (typeof args.message !== 'string' || args.message.trim().length === 0) {
    return validationError('message is required')
  }

  const targetId =
    typeof args.parentCommentId === 'string' && args.parentCommentId.length > 0
      ? args.parentCommentId
      : args.providerPostId

  const params = new URLSearchParams({ message: args.message })
  appendProviderOptions(params, args.providerOptions)

  const result = await mutateCommentEndpoint(
    targetId,
    params,
    pageAccessToken,
    ctx,
    deps,
    'createComment',
  )
  if (!result.ok) return result
  if (typeof result.data.id !== 'string' || result.data.id.length === 0) {
    return unknownError('Facebook Graph create comment returned no id')
  }

  return {
    ok: true,
    data: {
      providerCommentId: result.data.id,
      providerPostId: args.providerPostId,
      ...(args.parentCommentId ? { parentCommentId: args.parentCommentId } : {}),
      createdAt: (deps.now ?? (() => new Date()))().toISOString(),
    },
  }
}

export async function updateComment(
  args: SocialUpdateCommentArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPageCommentsDeps = {},
): Promise<SocialResult<SocialUpdateCommentResult>> {
  const validation = validateTokenAndComment(args.providerCommentId, args.providerPostId, args.targetAccountId, pageAccessToken)
  if (!validation.ok) return validation
  if (typeof args.message !== 'string' || args.message.trim().length === 0) {
    return validationError('message is required')
  }

  const result = await mutateCommentEndpoint(
    args.providerCommentId,
    new URLSearchParams({ message: args.message }),
    pageAccessToken,
    ctx,
    deps,
    'updateComment',
  )
  if (!result.ok) return result
  if (result.data.success !== true) {
    return unknownError('Facebook Graph update comment did not return success: true')
  }

  return {
    ok: true,
    data: {
      providerCommentId: args.providerCommentId,
      providerPostId: args.providerPostId,
      updatedAt: (deps.now ?? (() => new Date()))().toISOString(),
    },
  }
}

export async function deleteComment(
  args: SocialDeleteCommentArgs,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPageCommentsDeps = {},
): Promise<SocialResult<SocialDeleteCommentResult>> {
  const validation = validateTokenAndComment(args.providerCommentId, args.providerPostId, args.targetAccountId, pageAccessToken)
  if (!validation.ok) return validation

  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<SocialDeleteCommentResult>

  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  let response: Response
  try {
    response = await graphFetch(
      `${base}/${encodeURIComponent(args.providerCommentId)}`,
      pageAccessToken,
      deps.fetchImpl,
      { method: 'DELETE' },
    )
  } catch (err) {
    ctx.redactedLogger.error('deleteComment network error', {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return transientError()
  }

  if (!response.ok) {
    return { ok: false, error: await errorFromResponse(response, 'Facebook Graph delete comment failed') }
  }
  const body = (await response.json().catch(() => null)) as FacebookPageDeleteResponse | null
  if (!body || body.success !== true) {
    return unknownError('Facebook Graph delete comment did not return success: true')
  }

  return {
    ok: true,
    data: {
      providerCommentId: args.providerCommentId,
      providerPostId: args.providerPostId,
      deletedAt: (deps.now ?? (() => new Date()))().toISOString(),
    },
  }
}

async function mutateCommentEndpoint(
  targetId: string,
  params: URLSearchParams,
  pageAccessToken: string,
  ctx: SocialPostingCallCtx,
  deps: FacebookPageCommentsDeps,
  label: string,
): Promise<SocialResult<FacebookPageCommentMutationResponse>> {
  const gate = await checkRateLimit(ctx, ENDPOINT_LABEL, deps.rateLimitCheck)
  if (!gate.ok) return gate as SocialResult<FacebookPageCommentMutationResponse>

  const base = deps.graphApiBase ?? FB_PAGE_GRAPH_API_BASE
  const url = `${base}/${encodeURIComponent(targetId)}${label === 'createComment' ? '/comments' : ''}`

  let response: Response
  try {
    response = await graphFetch(url, pageAccessToken, deps.fetchImpl, {
      method: 'POST',
      body: params.toString(),
    })
  } catch (err) {
    ctx.redactedLogger.error(`${label} network error`, {
      error: err instanceof Error ? err.message : 'unknown',
    })
    return transientError()
  }

  if (!response.ok) {
    return { ok: false, error: await errorFromResponse(response, `Facebook Graph ${label} failed`) }
  }

  const body = (await response.json().catch(() => null)) as FacebookPageCommentMutationResponse | null
  if (!body || typeof body !== 'object') {
    return unknownError(`Facebook Graph ${label} returned an invalid body`)
  }
  return { ok: true, data: body }
}

async function graphFetch(
  url: string,
  token: string,
  fetchImpl: typeof fetch | undefined,
  init: RequestInit = {},
): Promise<Response> {
  return (fetchImpl ?? fetch)(url, {
    ...init,
    method: init.method ?? 'GET',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/x-www-form-urlencoded' } : {}),
      Authorization: `Bearer ${token}`,
      ...(init.headers as Record<string, string> | undefined),
    },
  })
}

function toSocialComment(row: FacebookPageCommentRow, providerPostId: string): SocialComment {
  const out: SocialComment = {
    providerCommentId: row.id,
    providerPostId,
  }
  if (typeof row.message === 'string') out.message = row.message
  if (typeof row.created_time === 'string') out.createdAt = row.created_time
  if (typeof row.from?.id === 'string') out.authorId = row.from.id
  if (typeof row.from?.name === 'string') out.authorName = row.from.name
  const authorAvatarUrl = extractAuthorAvatarUrl(row.from?.picture)
  if (authorAvatarUrl) out.authorAvatarUrl = authorAvatarUrl
  if (typeof row.like_count === 'number') out.likeCount = row.like_count
  if (typeof row.comment_count === 'number') out.replyCount = row.comment_count
  if (typeof row.parent?.id === 'string') out.parentCommentId = row.parent.id
  if (typeof row.permalink_url === 'string') out.permalinkUrl = row.permalink_url
  if (typeof row.can_remove === 'boolean') out.canRemove = row.can_remove
  if (typeof row.can_comment === 'boolean') out.canComment = row.can_comment
  return out
}

function extractAuthorAvatarUrl(picture: { data?: { url?: string } } | string | undefined): string | undefined {
  if (typeof picture === 'string' && picture.length > 0) return picture
  if (!picture || typeof picture !== 'object') return undefined
  const url = picture?.data?.url
  return typeof url === 'string' && url.length > 0 ? url : undefined
}

function appendProviderOptions(params: URLSearchParams, options: Record<string, unknown> | undefined): void {
  if (!options) return
  for (const [k, v] of Object.entries(options)) {
    if (typeof v === 'string') params.set(k, v)
    else if (typeof v === 'number' || typeof v === 'boolean') params.set(k, String(v))
  }
}

function validateTokenAndPost(
  providerPostId: string,
  targetAccountId: string,
  token: string,
): SocialResult<true> {
  if (typeof token !== 'string' || token.length === 0) {
    return { ok: false, error: { code: 'auth', message: 'Facebook Page access token is missing', retryable: false } }
  }
  if (typeof providerPostId !== 'string' || providerPostId.length === 0) {
    return validationError('providerPostId is required')
  }
  if (typeof targetAccountId !== 'string' || targetAccountId.length === 0) {
    return validationError('targetAccountId is required')
  }
  return { ok: true, data: true }
}

function validateTokenAndComment(
  providerCommentId: string,
  providerPostId: string,
  targetAccountId: string,
  token: string,
): SocialResult<true> {
  const base = validateTokenAndPost(providerPostId, targetAccountId, token)
  if (!base.ok) return base
  if (typeof providerCommentId !== 'string' || providerCommentId.length === 0) {
    return validationError('providerCommentId is required')
  }
  return { ok: true, data: true }
}

function clampLimit(limit: number | undefined): number {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) return DEFAULT_LIMIT
  return Math.max(1, Math.min(MAX_LIMIT, Math.floor(limit)))
}

async function checkRateLimit(
  ctx: SocialPostingCallCtx,
  endpoint: string,
  rateLimitCheck: RateLimitCheck | undefined,
): Promise<SocialResult<true>> {
  if (!rateLimitCheck) return { ok: true, data: true }
  const decision = await rateLimitCheck({ instanceId: ctx.instanceId, endpoint }, ctx)
  if (decision.allowed) return { ok: true, data: true }
  return {
    ok: false,
    error: {
      code: 'rate_limit',
      message: `Rate limit exceeded for ${endpoint}`,
      retryable: true,
      retryAfterSeconds: decision.retryAfterSeconds,
    },
  }
}

function validationError(message: string): SocialResult<never> {
  return {
    ok: false,
    error: {
      code: 'provider_rejected',
      message: sanitiseMetaErrorString(message),
      retryable: false,
    },
  }
}

function transientError(): SocialResult<never> {
  return {
    ok: false,
    error: {
      code: 'transient',
      message: 'Network error talking to Facebook Graph',
      retryable: true,
    },
  }
}

function unknownError(message: string): SocialResult<never> {
  return {
    ok: false,
    error: {
      code: 'unknown',
      message,
      retryable: false,
    },
  }
}
